import { logger } from './logger';
import { FileAttachment, SUPPORTED_FILE_TYPES } from '@/types/agent';
// zemaxContainer.js (not api.js) on purpose: api.js pulls in Node's fs/path/os
// at module scope for the file-based converter functions, which Turbopack
// cannot bundle for the browser. zemaxContainer.js has zero imports of its own.
import { ZEMAX_IMPORT_EXTENSIONS } from '../../zemax-converter/dist/converters/zemaxContainer.js';
// Same reasoning: seqExtensions.js is the CODE V converter's zero-import module.
import { CODEV_IMPORT_EXTENSIONS } from '../../zemax-converter/dist/codev/seqExtensions.js';

const ZEMAX_IMPORT_EXTENSION_SET = new Set<string>(ZEMAX_IMPORT_EXTENSIONS);

// Chat-attachment limits. The server is authoritative for all four; these copies
// only let the picker reject at pick time instead of after an upload.
//
// The two API ceilings below are PINNED: fileUtils.test.ts imports them from
// src/agent/agentReadableFiles and asserts equality, so drift fails the build.
// MAX_FILE_ATTACHMENTS and MAX_FILE_SIZE are NOT — they live in
// src/server/types.ts (MAX_FILE_ATTACHMENTS, MAX_FILE_ATTACHMENT_SIZE_BYTES),
// which this test cannot import (it pulls zod and a .js-suffixed server graph
// that will not resolve under moduleResolution "bundler").
export const MAX_FILE_ATTACHMENTS = 5;
export const MAX_FILE_SIZE = 10 * 1024 * 1024;
export const MAX_IMAGE_RAW_BYTES = 7_864_320;
export const MAX_TOTAL_ATTACHMENT_BASE64_BYTES = 20 * 1024 * 1024;

/** base64 encodes 3 bytes as 4 chars, padded to a 4-char boundary. */
function base64LengthOf(rawBytes: number): number {
  return Math.ceil(rawBytes / 3) * 4;
}

// File-library size caps. The frontend has no shared package with src/, so these
// MIRROR their server constants — keep in sync if the server limits change:
//   PROJECT_FILE_MAX_BYTES  → src/server/constants.ts
//   FIRM_FILE_MAX_BYTES     → src/services/firmPlaybooks/fileManager.ts (MAX_FIRM_FILE_BYTES)
export const PROJECT_FILE_MAX_BYTES = 50 * 1024 * 1024;
export const FIRM_FILE_MAX_BYTES = 32 * 1024 * 1024;

/** Returns a human-readable error if the file exceeds maxBytes, else null. */
export function fileTooLargeMessage(file: File, maxBytes: number): string | null {
  if (file.size <= maxBytes) return null;
  return `${file.name} is ${formatFileSize(file.size)} — the limit is ${formatFileSize(maxBytes)}.`;
}

export const SUPPORTED_MIME_TYPES = new Set<string>([
  ...SUPPORTED_FILE_TYPES.IMAGE,
  ...SUPPORTED_FILE_TYPES.DOCUMENT,
  ...SUPPORTED_FILE_TYPES.NOTEBOOK,
  ...SUPPORTED_FILE_TYPES.TEXT,
]);

// Mirrors AGENT_READABLE_TEXT_EXTENSIONS in src/agent/agentReadableFiles.ts —
// every extension the server will read as text must be accepted here, or the
// picker rejects a file the identical bytes of which would validate and inline
// server-side. Pinned in BOTH directions by fileUtils.test.ts (set equality
// against the server list). Almost everything rides as text/plain; the overrides
// below map the few extensions with a more specific MIME in SUPPORTED_MIME_TYPES.
//
// .svg is included: browsers type it image/svg+xml, which SUPPORTED_MIME_TYPES
// rejects, so resolveSupportedFileType falls back to this map and it rides as
// text/plain — matching the server, which reads SVG markup as text.
const PLAIN_TEXT_EXTENSIONS = [
  '.txt', '.rst', '.log', '.tsv', '.jsonl', '.xml', '.yaml', '.yml',
  '.toml', '.ini', '.cfg', '.conf',
  '.py', '.js', '.mjs', '.cjs', '.ts', '.tsx', '.jsx',
  '.c', '.h', '.cpp', '.cc', '.hpp', '.m', '.java', '.rs', '.go',
  '.sh', '.bash', '.zsh', '.sql', '.r', '.jl', '.f', '.f90',
  '.html', '.htm', '.css', '.svg',
  '.zpl', '.agf', '.seq',
];

const EXTENSION_MIME_OVERRIDES: Record<string, string> = {
  '.ipynb': 'application/x-ipynb+json',
  '.md': 'text/markdown',
  '.markdown': 'text/markdown',
  '.csv': 'text/csv',
  '.json': 'application/json',
};

export const EXTENSION_MIME_TYPES: Record<string, string> = {
  ...Object.fromEntries(PLAIN_TEXT_EXTENSIONS.map((ext) => [ext, 'text/plain'])),
  ...EXTENSION_MIME_OVERRIDES,
};

/** `accept` for the chat file input — kept in lockstep with the map above so
 *  the picker offers what the client (and server) will take. Still slightly
 *  over-offers via image/*: a picker can surface formats the API rejects
 *  (e.g. HEIC), which validateFile then refuses with a clear message. */
export const CHAT_ACCEPT_ATTRIBUTE = ['image/*', '.pdf', ...ZEMAX_IMPORT_EXTENSIONS, ...Object.keys(EXTENSION_MIME_TYPES)].join(',');

/** `accept` for the project/library file pickers. The library stores anything —
 *  accept is a soft filter, not validation — this only stops the picker
 *  under-offering formats the agent reads. Chat accepts the same set, so this
 *  delegates to CHAT_ACCEPT_ATTRIBUTE directly. */
export const PROJECT_FILE_ACCEPT_ATTRIBUTE = CHAT_ACCEPT_ATTRIBUTE;

/** CAD assembly formats the import flow parses into a sequential prescription
 *  (analysis-service `/cad-import/parse`): STEP and IGES. Unlike .json these
 *  need no sniff — the extension alone decides, because nothing else in the
 *  picker claims it. The ONE browser-side list; its twins are
 *  CAD_IMPORT_EXTENSIONS in src/server/constants.ts and
 *  `services.cad.load.CAD_EXTENSIONS` in the analysis service. The name
 *  predates IGES and is the path's, not the file's. */
export const STEP_IMPORT_EXTENSIONS = ['.step', '.stp', '.igs', '.iges'] as const;

/** `accept` for ImportButton's picker, which routes a Zemax, CODE V, JSON or CAD
 *  upload through the import→confirm flow instead of the plain chat-attachment
 *  path. Mirrors IMPORT_FORMATS in src/server/constants.ts. */
export const SYSTEM_IMPORT_ACCEPT_ATTRIBUTE = [...ZEMAX_IMPORT_EXTENSIONS, ...CODEV_IMPORT_EXTENSIONS, '.json', ...STEP_IMPORT_EXTENSIONS].join(',');

/** Size cap on the import flow — mirrors the server's import limit; the
 *  preview upload rejects anything larger anyway. */
export const SYSTEM_IMPORT_MAX_BYTES = 50 * 1024 * 1024;

/** Cap for CAD (STEP or IGES), above the generic one: a CAD assembly with its mount bodies
 *  is far larger than any prescription file, and the Node import branch
 *  streams the bytes to the analysis-service as a multipart part (no base64
 *  inflation), so the whole chain carries the same number. MIRRORS
 *  CAD_IMPORT_MAX_BYTES in src/server/constants.ts and CAD_MAX_STEP_BYTES in
 *  analysis-service/Dockerfile — keep the three in sync. */
export const STEP_IMPORT_MAX_BYTES = 100 * 1024 * 1024;

const SYSTEM_IMPORT_EXTENSION_SET = new Set<string>([...ZEMAX_IMPORT_EXTENSIONS, ...CODEV_IMPORT_EXTENSIONS, '.json', ...STEP_IMPORT_EXTENSIONS]);

const STEP_IMPORT_EXTENSION_SET = new Set<string>(STEP_IMPORT_EXTENSIONS);

export function isStepImportFileName(fileName: string): boolean {
  return STEP_IMPORT_EXTENSION_SET.has(extensionOf(fileName));
}

/** The import cap that applies to this file — a CAD file is capped higher than
 *  the rest (see STEP_IMPORT_MAX_BYTES). */
export function systemImportMaxBytes(fileName: string): number {
  return isStepImportFileName(fileName) ? STEP_IMPORT_MAX_BYTES : SYSTEM_IMPORT_MAX_BYTES;
}

/** Lowercased extension including the dot; a dotless name yields no usable
 *  extension (its last character), which every lookup below rejects. */
const extensionOf = (fileName: string) => fileName.slice(fileName.lastIndexOf('.')).toLowerCase();

/** Whether a picked file should enter the system-import flow rather than ride
 *  the message as a plain attachment. Zemax and CODE V formats always import; a .json
 *  imports only when it holds one of the JSON system formats the import
 *  pipeline accepts — sniffed with the converter's own parseJsonSystem, so
 *  this routing can never disagree with the server. A .json that is no system
 *  (tolerance data, a spec sheet, broken JSON) attaches instead. A CAD file
 *  (STEP_IMPORT_EXTENSIONS) always imports, unsniffed and whatever its size: the CAD parse happens
 *  server-side, and an oversized one must still reach the import flow to be
 *  told it is too large rather than ride along as an unreadable attachment. */
export async function isImportableSystemFile(file: File): Promise<boolean> {
  const ext = extensionOf(file.name);
  if (!SYSTEM_IMPORT_EXTENSION_SET.has(ext)) return false;
  if (ext !== '.json') return true;
  // A .json past the import cap can never import, so don't read it into
  // memory just to sniff it — the attach path's validateFile reports the size.
  if (file.size > SYSTEM_IMPORT_MAX_BYTES) return false;
  try {
    // Dynamic import: jsonSystemDetection pulls the converter's zod schema
    // graph, which no page should pay for until a .json is actually staged.
    const { parseJsonSystem } = await import(
      '../../zemax-converter/dist/converters/jsonSystemDetection.js'
    );
    return parseJsonSystem(await file.text()) !== null;
  } catch {
    // Unparseable text is no system — parseJsonSystem throws on it by
    // contract. A failed chunk load (stale tab after a deploy, offline)
    // lands here too and degrades to the attach path rather than a dead pick.
    return false;
  }
}

function getSupportedTypeFromExtension(fileName?: string): string | null {
  if (!fileName) return null;
  const lowerName = fileName.toLowerCase();
  for (const [extension, mimeType] of Object.entries(EXTENSION_MIME_TYPES)) {
    if (lowerName.endsWith(extension)) return mimeType;
  }
  return null;
}

/** Mirrors canonicalMimeType in src/agent/agentReadableFiles.ts. Without it,
 *  getFileCategory does not see an image/jpg as an image and it dodges the
 *  image ceiling client-side. */
export function canonicalMimeType(type: string): string {
  const lower = type.toLowerCase();
  return lower === 'image/jpg' ? 'image/jpeg' : lower;
}

function resolveSupportedFileType(type: string, fileName?: string): string | null {
  const canonical = canonicalMimeType(type);
  if (SUPPORTED_MIME_TYPES.has(canonical)) {
    return canonical;
  }

  // Zemax system files (.zmx, .zos, .zar, .zprj): binary, never read as text by
  // the agent, but accepted as a chat attachment so they can be mirrored into the
  // project library and later imported via importSystemFromFile.
  const extension = fileName ? extensionOf(fileName) : '';
  if (ZEMAX_IMPORT_EXTENSION_SET.has(extension)) {
    return 'application/x-zemax';
  }

  return getSupportedTypeFromExtension(fileName);
}

function isSupportedFileType(type: string, fileName?: string): boolean {
  return resolveSupportedFileType(type, fileName) !== null;
}

/** Widens the `as const` tuples so a runtime string can be looked up in them. */
function isSupportedMime(type: string, supportedTypes: readonly string[]): boolean {
  return supportedTypes.includes(type);
}

/**
 * Get file type category
 */
export type FileCategory = 'image' | 'document' | 'notebook' | 'text' | 'unknown';

export function getFileCategory(type: string, fileName?: string): FileCategory {
  // No `?? type` fallback: an unresolved type is in none of the lists below by
  // construction, so it can only be 'unknown' either way.
  const resolvedType = resolveSupportedFileType(type, fileName);
  if (resolvedType === null) return 'unknown';
  if (isSupportedMime(resolvedType, SUPPORTED_FILE_TYPES.IMAGE)) return 'image';
  if (isSupportedMime(resolvedType, SUPPORTED_FILE_TYPES.DOCUMENT)) return 'document';
  if (isSupportedMime(resolvedType, SUPPORTED_FILE_TYPES.TEXT)) return 'text';
  if (isSupportedMime(resolvedType, SUPPORTED_FILE_TYPES.NOTEBOOK)) return 'notebook';
  return 'unknown';
}

export function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = () => {
      try {
        const result = reader.result as string;
        if (!result) {
          reject(new Error('FileReader returned empty result'));
          return;
        }
        // Remove the data URL prefix (e.g., "data:image/png;base64,")
        const base64 = result.split(',')[1];
        if (!base64) {
          reject(new Error('Failed to extract base64 data from FileReader result'));
          return;
        }
        resolve(base64);
      } catch (error) {
        reject(new Error(`Failed to process FileReader result: ${error instanceof Error ? error.message : 'Unknown error'}`));
      }
    };

    reader.onerror = () => {
      const errorMsg = reader.error?.message || 'FileReader failed';
      reject(new Error(`FileReader error: ${errorMsg}`));
    };

    reader.onabort = () => {
      reject(new Error('FileReader was aborted'));
    };

    // Start reading
    try {
      reader.readAsDataURL(file);
    } catch (error) {
      reject(new Error(`Failed to start FileReader: ${error instanceof Error ? error.message : 'Unknown error'}`));
    }
  });
}

function generateImagePreview(file: File): Promise<string | undefined> {
  return new Promise((resolve) => {
    if (getFileCategory(file.type, file.name) !== 'image') {
      resolve(undefined);
      return;
    }

    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => resolve(undefined);
  });
}

export function validateFile(file: File): { valid: boolean; error?: string } {
  if (!isSupportedFileType(file.type, file.name)) {
    return {
      valid: false,
      error: 'Unsupported file type. Supported: Images (PNG, JPG, GIF, WebP), PDFs, Jupyter notebooks (.ipynb), text/code/data files'
    };
  }

  // The server rejects empty files (size min 1); catching it here gives a clear
  // message instead of FileReader's "returned empty result".
  if (file.size === 0) {
    return { valid: false, error: `File "${file.name}" is empty (0 bytes)` };
  }

  // One check against the binding limit, so the message always names the limit
  // that actually applies. Checking the generic cap first would tell someone
  // with an 11MB PNG "maximum 10MB", and only reveal the real 7.5MB image
  // ceiling after they had shrunk it and resubmitted.
  const maxBytes = getFileCategory(file.type, file.name) === 'image'
    ? MAX_IMAGE_RAW_BYTES
    : MAX_FILE_SIZE;
  if (file.size > maxBytes) {
    return {
      valid: false,
      error: `File "${file.name}" is ${formatFileSize(file.size)}. Maximum size is ${formatFileSize(maxBytes)}`
    };
  }

  return { valid: true };
}

async function processFile(file: File): Promise<FileAttachment> {
  logger.info(`[File Processing] Processing file:`, {
    name: file.name,
    type: file.type,
    size: formatFileSize(file.size)
  });

  const validation = validateFile(file);
  if (!validation.valid) {
    throw new Error(validation.error);
  }

  const [base64Data, preview] = await Promise.all([
    fileToBase64(file),
    generateImagePreview(file)
  ]);

  logger.info(`[File Processing] Successfully processed "${file.name}" (${formatFileSize(file.size)})`);

  // Non-null: validateFile above throws unless resolveSupportedFileType — a pure
  // function of these same inputs — returns a type. No fallback needed.
  const resolvedType = resolveSupportedFileType(file.type, file.name)!;
  return {
    id: crypto.randomUUID(),
    name: file.name,
    type: resolvedType,
    size: file.size,
    data: base64Data,
    preview
  };
}

export interface ProcessFilesResult {
  files: FileAttachment[];
  /** Per-file failures ("name: reason"), so callers can surface a partial
   *  failure instead of silently attaching a subset of what the user picked. */
  errors: string[];
}

/**
 * Process multiple files. Batch-level violations (attachment count, total
 * request budget) throw — no subset of such a batch is sendable. Per-file
 * failures are returned in `errors` alongside the files that succeeded.
 */
export async function processFiles(files: File[], existing: FileAttachment[] = []): Promise<ProcessFilesResult> {
  if (existing.length + files.length > MAX_FILE_ATTACHMENTS) {
    throw new Error(`Cannot attach more than ${MAX_FILE_ATTACHMENTS} files`);
  }

  // The request carries every attachment at once, so the budget spans the ones
  // already staged too — hence the whole list, not a count. Checked up front
  // because it is a property of the BATCH: the per-file loop below tolerates
  // individual failures and reports them per file, which would silently accept
  // a subset that still cannot be sent.
  const totalBase64 = [...existing, ...files]
    .reduce((sum, f) => sum + base64LengthOf(f.size), 0);
  if (totalBase64 > MAX_TOTAL_ATTACHMENT_BASE64_BYTES) {
    // Quote the encoded size, as the server does: a user looking at 16MB of
    // files told they exceed a "20MB limit" reads that as a bug.
    throw new Error(
      `Attachments total ${formatFileSize(totalBase64)} encoded, over the `
      + `${formatFileSize(MAX_TOTAL_ATTACHMENT_BASE64_BYTES)} total limit for one message`
    );
  }

  const processedFiles: FileAttachment[] = [];
  const errors: string[] = [];

  for (const file of files) {
    try {
      const processed = await processFile(file);
      processedFiles.push(processed);
    } catch (error) {
      const errorMsg = error instanceof Error ? error.message : 'Unknown error';
      // Expected rejections (unsupported type, too large) land here too, so
      // warn — the caller's inline banner is the user-facing signal.
      logger.warn(`[File Processing] Failed to process "${file.name}" (${file.type}, ${formatFileSize(file.size)}):`, error);
      errors.push(`${file.name}: ${errorMsg}`);
    }
  }

  return { files: processedFiles, errors };
}

export function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 Bytes';

  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
}

/**
 * Trigger a browser download of an in-memory blob.
 *
 * The revoke is deferred: Safari treats `a.click()` as asynchronous, and
 * revoking the object URL synchronously can produce an empty download.
 */
export function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export function base64ToBytes(base64: string): Uint8Array<ArrayBuffer> {
  return Uint8Array.from(atob(base64), (ch) => ch.charCodeAt(0));
}

/** Download base64 bytes a worker produced (a `.zmx`, a `.TOL`) as a file. */
export function downloadBase64(base64: string, filename: string): void {
  downloadBlob(new Blob([base64ToBytes(base64)], { type: 'application/octet-stream' }), filename);
}

